import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { fetchApps, fetchCategories } from "@/lib/api";
import { AppCard } from "@/components/AppCard";
import Footer from "@/components/Footer";
import { BROWSE } from "@/constants/testIds";
import { Filter, X } from "lucide-react";

const SORTS = [
  { id: "trending", label: "Trending" },
  { id: "new", label: "New" },
  { id: "top", label: "Top Rated" },
];
const PRICES = [
  { id: "", label: "All" },
  { id: "free", label: "Free" },
  { id: "paid", label: "Paid" },
  { id: "open", label: "Open Source" },
];

export default function Browse() {
  const { id: catFromRoute } = useParams();
  const [sp, setSp] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);

  const category = catFromRoute || sp.get("category") || "all";
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "trending";
  const price = sp.get("price") || "";

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchApps({
      category: category === "all" ? undefined : category,
      q: q || undefined,
      sort,
      price: price || undefined,
    })
      .then((d) => setApps(d))
      .finally(() => setLoading(false));
  }, [category, q, sort, price]);

  const update = (key, val) => {
    const next = new URLSearchParams(sp);
    if (!val) next.delete(key);
    else next.set(key, val);
    setSp(next);
  };

  const activeCat = cats.find((c) => c.id === category);

  return (
    <div data-testid={BROWSE.root}>
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">
          // {activeCat ? `category / ${activeCat.name.toLowerCase()}` : "browse"}
        </div>
        <h1 className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tighter">
          {q ? (
            <>
              Results for <span className="text-[#00F0FF]">&ldquo;{q}&rdquo;</span>
            </>
          ) : activeCat ? (
            activeCat.name
          ) : (
            "Browse Apps"
          )}
        </h1>
        <p className="text-white/50 mt-2">{apps.length} apps</p>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-white/40 mr-2">
            <Filter className="w-3 h-3" /> Filters
          </div>

          <div data-testid={BROWSE.filterCategory} className="flex gap-1 overflow-x-auto hide-scrollbar max-w-full">
            <Chip active={category === "all"} onClick={() => update("category", "")}>
              All
            </Chip>
            {cats.map((c) => (
              <Chip
                key={c.id}
                active={category === c.id}
                onClick={() => update("category", c.id)}
              >
                {c.name}
              </Chip>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div data-testid={BROWSE.filterSort} className="flex gap-1">
            {SORTS.map((s) => (
              <Chip
                key={s.id}
                active={sort === s.id}
                accent="#39FF14"
                onClick={() => update("sort", s.id)}
              >
                {s.label}
              </Chip>
            ))}
          </div>
          <div className="w-px h-6 bg-white/10 mx-1" />
          <div data-testid={BROWSE.filterPrice} className="flex gap-1">
            {PRICES.map((p) => (
              <Chip
                key={p.id}
                active={price === p.id}
                accent="#FFE300"
                onClick={() => update("price", p.id)}
              >
                {p.label}
              </Chip>
            ))}
          </div>
          {(category !== "all" || q || sort !== "trending" || price) && (
            <button
              onClick={() => setSp(new URLSearchParams())}
              className="mono text-[10px] uppercase text-white/50 hover:text-[#FF003C] flex items-center gap-1 ml-2"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {/* Results */}
        <div data-testid={BROWSE.results} className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading && (
            <div className="col-span-full mono text-sm text-[#00F0FF] animate-pulse">Loading…</div>
          )}
          {!loading && apps.length === 0 && (
            <div className="col-span-full py-20 text-center">
              <div className="mono text-xs uppercase tracking-widest text-white/40 mb-2">// no results</div>
              <p className="text-white/60">Nothing matches your filters. Try clearing them.</p>
            </div>
          )}
          {apps.map((a) => (
            <AppCard key={a.slug} app={a} />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Chip({ children, active, onClick, accent = "#00F0FF" }) {
  return (
    <button
      onClick={onClick}
      className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap"
      style={{
        borderColor: active ? accent : "rgba(255,255,255,0.1)",
        color: active ? accent : "rgba(255,255,255,0.7)",
        background: active ? `${accent}12` : "transparent",
      }}
    >
      {children}
    </button>
  );
}
